import {IAppModel, ICalendar, IDeliveryStatus, IDeliveryType, ILastDelivery, IRootScope, ITemplate, ITemplateDeliveryTypes} from "../hpc";
import {ILogService} from "angular";
import * as restangular from "restangular";
import * as _ from "underscore";
import {TrackerService} from "./TrackerService";

class Calendar implements ICalendar {
	id: number;
	version: number;
	errorMessage: string;
	begin: number;
	end: number;
	lastChanged: number;
	deliveryTypes: IDeliveryType[];
	deliveryStatus: IDeliveryStatus;
}

export class DeliveryService {

	public static $inject = ['$rootScope','$moment', '$localStorage', '$sessionStorage', '$log', 'tracker', 'Restangular'];

	announcements = this.Rest.all('announcements');

	constructor(
		private $rs: IRootScope,
		private $moment,
		private $localStorage: any,
		private appModel: IAppModel,
		private $log: ILogService,
		private tracker: TrackerService,
		private Rest: restangular.IService,
	) {
	};

	public async activateAnnouncement(announcement, begin, end, deliveryTypes): Promise<void> {
		this.$rs.errormessage = "";
		this.$rs.showLoading = true;
		this.tracker.trackEvent('Announcement', 'Activation', announcement.name);
		let calendar = this.createCalendar(begin,  end, deliveryTypes);

		try {
			await this.announcements.one(this.appModel.currentAnnouncement.id.toString()).post('calendars', calendar, {'announcementVersion': this.appModel.currentAnnouncement.version});
			this.appModel.currentAnnouncement = null;
			this.appModel.deliveryPending = true;
			this.$log.info('Announcement aktiviert');
			if (end) {
				await this.createFollowUpAnnouncement(begin, end, deliveryTypes);
			}
			this.$rs.$broadcast('calendarRefresh');
		} catch (res) {
			this.$log.error('Error Activating Announcement');
			throw res;
		}
	}

	private async createFollowUpAnnouncement(begin, end, deliveryTypes): Promise<void> {
		this.$rs.showLoading = true;
		var followUpAnnouncement: ILastDelivery = await this.ensureDefaultAnnouncement();
		this.appModel.lastDelivery = null; // switching on loading
		if (!this.isValidFollowUpAnnouncement(followUpAnnouncement, deliveryTypes)) {
			this.$rs.showLoading = false;
			this.$rs.errormessage = "WARNING_NO_FOLLOW_UP_FOUND";
			return;
		}
		this.tracker.trackEvent('Announcement', 'followUpActivation', followUpAnnouncement.name);
		try {
			await this.announcements.one(followUpAnnouncement.id.toString()).post('calendars', this.createCalendar(end, null, deliveryTypes), {
				'announcementVersion': followUpAnnouncement.version
			});
			this.$log.info('Follow Up Annoncement created');
		} catch (res) {
			this.$log.error('Error creating Followup Announcement');
			throw res;
		}
	}

	private async ensureDefaultAnnouncement(): Promise<ILastDelivery> {
		const defaultAnnouncement = await this.Rest.one('/accounts/me/').post('defaultAnnouncement/ensure');
		await this.provideTemplateForFollowUpAnnouncement(defaultAnnouncement);
		this.appModel.storedAnnouncements = this.appModel.storedAnnouncements || {defaultAnnouncement: undefined, oooAnnouncement: undefined};
		this.appModel.storedAnnouncements.defaultAnnouncement = defaultAnnouncement;
		return defaultAnnouncement;
	}

	private async provideTemplateForFollowUpAnnouncement(announcement: ILastDelivery): Promise<void> {
		if (!announcement || announcement.template?.templateDeliveryTypes || !announcement.templateId) {
			return;
		}

		const cachedTemplate = _.find(this.appModel.templates || [], (template: ITemplate) => {
			return template.id === announcement.templateId && !!template.templateDeliveryTypes;
		});
		announcement.template = cachedTemplate || await this.Rest.one('templates', announcement.templateId).get();
	}

	public findFollowUpAnnouncement(begin, deliveryTypes) {
		if (!this.appModel || !this.appModel.lastDelivery) {
			return null;
		}
		var followUpAnnouncement: ILastDelivery = null;
		this.checkDeliveryTypesValidForFollowUp(this.appModel.lastDelivery.calendar?.deliveryTypes, deliveryTypes) ? followUpAnnouncement = this.appModel.lastDelivery : null;
		var maxBegin: number = 0;
		_.each(this.appModel.announcements, (announcement: ILastDelivery) => {
			if (announcement.calendar.begin > maxBegin && announcement.calendar.begin < begin.getTime() && this.checkDeliveryTypesValidForFollowUp(announcement.calendar.deliveryTypes, deliveryTypes)) {
				followUpAnnouncement = announcement;
				maxBegin = announcement.calendar.begin;
			}
		});
		return followUpAnnouncement;
	}

	private checkDeliveryTypesValidForFollowUp(available: IDeliveryType[], deliveryTypes): boolean {
		var requestedIds = [];
		var availableIds = [];
		_.each(deliveryTypes, (tdt: ITemplateDeliveryTypes) => {
			if (tdt.selected) {
				requestedIds.push(tdt.deliveryType.id)
			}
		});
		_.each(available, (dt: IDeliveryType) => {
			availableIds.push(dt.id);
		});

		return _.intersection(requestedIds, availableIds).length === requestedIds.length;

	}

	public hasFollowUpAnnouncement(begin, deliveryTypes): boolean {
		return this.isValidFollowUpAnnouncement(this.appModel?.storedAnnouncements?.defaultAnnouncement, deliveryTypes)
			|| !!this.findFollowUpAnnouncement(begin, deliveryTypes);
	}

	private isValidFollowUpAnnouncement(announcement: ILastDelivery, deliveryTypes): boolean {
		return !!announcement && this.checkDeliveryTypesValidForFollowUp(this.getFollowUpDeliveryTypes(announcement), deliveryTypes);
	}

	private getFollowUpDeliveryTypes(announcement: ILastDelivery): IDeliveryType[] {
		if (announcement.template?.templateDeliveryTypes) {
			var templateDeliveryTypes: IDeliveryType[] = [];
			_.each(announcement.template.templateDeliveryTypes, (tdt: ITemplateDeliveryTypes) => {
				templateDeliveryTypes.push(tdt.deliveryType);
			});
			return templateDeliveryTypes;
		}
		if (announcement.calendar?.deliveryTypes) {
			return announcement.calendar.deliveryTypes;
		}
		return [];
	}

	private createCalendar(begin: Date, end: Date, deliveryTypes) {
		var cal: ICalendar = new Calendar;
		cal.version = 0;
		if (!begin) {
			cal.begin = 0;
		} else {
			cal.begin = begin.getTime();
		}

		if(end) {
			cal.end = end.getTime();
		}

		cal.deliveryTypes = [];

		for (var i = 0; i < deliveryTypes.length; i++) {
			var tdt: ITemplateDeliveryTypes = deliveryTypes[i];
			if (tdt.selected) {
				cal.deliveryTypes.push(tdt.deliveryType);
			}
		}
		return cal;
	}


}
